'use client'

import { useEffect } from "react"
import { createClient } from "@/lib/supabase/client"
import { identifyUser, trackAction } from "../../lib/tracking"
import { AuthChangeEvent } from "@supabase/supabase-js"
import { mutate } from 'swr'

// handles auth state changes
export function Supabase() {
    const supabase = createClient();

    // Set up auth state change listener to identify users in PostHog
    useEffect(() => {
        // Check initial session
        const checkSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
                // Identify user with our tracking function
                identifyUser({
                    userId: session.user.id,
                    userProperties: {
                        email: session.user.email,
                        name: session.user.user_metadata?.full_name,
                        avatar_url: session.user.user_metadata?.avatar_url
                    }
                });
            }
        };
        
        checkSession();

        // Listen for auth state changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event: AuthChangeEvent, session) => {
                // Identify on initial session, sign in, and user updates
                if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'USER_UPDATED') && session?.user) {
                    // Identify user with our tracking function
                    identifyUser({
                        userId: session.user.id,
                        userProperties: {
                            email: session.user.email,
                            name: session.user.user_metadata?.full_name,
                            avatar_url: session.user.user_metadata?.avatar_url,
                            auth_event: event // Track which event triggered the identification
                        }
                    });

                    // Handle workspace creation for new users (only on SIGNED_IN events)
                    if (event === 'SIGNED_IN') {
                        await handleNewUserWorkspace(session.user);
                    }
                } else if (event === 'SIGNED_OUT') {
                    // Track sign out event
                    trackAction('User Signed Out', { event });
                }
            }
        );

        // Cleanup subscription on unmount
        return () => {
            subscription.unsubscribe();
        };
    }, [supabase]);

    return null;
}

/**
 * Handle workspace creation for new users using race-condition-safe endpoint
 * This runs silently in the background without affecting the user experience
 * Triggers workspace refetch after creation
 */
async function handleNewUserWorkspace(user: any) {
    try {
        // Skip if no email (shouldn't happen but safety check)
        if (!user.email) {
            console.log('User has no email, skipping workspace creation');
            return;
        }

        // Check if user already has an active workspace set in metadata
        const currentActiveWorkspaceId = user.user_metadata?.active_workspace_id;
        
        if (currentActiveWorkspaceId) {
            console.log('User already has active workspace in metadata:', currentActiveWorkspaceId);
            
            // Verify the workspace still exists and user has access
            try {
                const response = await fetch('/api/workspaces');
                if (response.ok) {
                    const data = await response.json();
                    const hasWorkspaceAccess = data.workspaces?.some((w: any) => w.id === currentActiveWorkspaceId);
                    
                    if (hasWorkspaceAccess) {
                        console.log('Active workspace is valid, skipping creation');
                        return;
                    } else {
                        console.log('Active workspace no longer accessible, will create new one');
                    }
                }
            } catch (error) {
                console.log('Could not verify existing workspace, will attempt creation');
            }
        }

        // Use the race-condition-safe endpoint to create default workspace
        console.log('Attempting to create default workspace for user:', user.email);
        
        const response = await fetch('/api/workspaces/create-default', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                description: 'Your default workspace'
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to create default workspace');
        }

        const result = await response.json();
        
        if (result.created) {
            console.log('Successfully created default workspace:', result.workspace.id);
            
            // Trigger global SWR refetch for workspaces
            await mutate('/api/workspaces');
            console.log('Triggered workspace refetch');
            
            // Track successful workspace creation
            trackAction('Default Workspace Created', {
                userId: user.id,
                workspaceId: result.workspace.id,
                authProvider: user.app_metadata?.provider || 'email'
            });
        } else {
            console.log('User already has workspaces, skipped creation');
            
            // Track that workspace creation was skipped
            trackAction('Default Workspace Creation Skipped', {
                userId: user.id,
                reason: 'User already has workspaces',
                authProvider: user.app_metadata?.provider || 'email'
            });
        }

    } catch (error) {
        // Log error but don't disrupt the user experience
        console.error('Failed to create default workspace:', error);
        
        // Track the error for debugging
        trackAction('Default Workspace Creation Failed', {
            userId: user.id,
            error: error instanceof Error ? error.message : 'Unknown error',
            authProvider: user.app_metadata?.provider || 'email'
        });
    }
}